import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Divider,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

// TRANSLATIONS (English / French) - condensed keys for clarity
const T = {
  en: {
    yes: "Yes",
    no: "No",
    pageTitle: "Express Entry Questionnaire - Principal Applicant",
    langLabel: "Language",
    // Profile details
    profileTitle: "Profile details",
    lastName: "Last name(s):",
    firstName: "First name(s):",
    gender: "Gender:",
    genderOptions: [
      "Male",
      "Female",
      "Others"
    ],
    dob: "Date of birth: mm-dd-yyyy",
    pob: "Place of birth: country/city(town)",
    marital: "Marital status:",
    maritalOptions: [
      "Never Married / Single",
      "Married",
      "Common-Law",
      "Legally Separated",
      "Divorced",
      "Widowed",
      "Annulled Marriage",
    ],
    accompany: "If you are in common-law or married, will your spouse/common-law partner accompany you to Canada?",

    // Passport
    passportTitle: "Passport / Travel documents",
    passportNumber: "Passport number:",
    passportCountry: "Country/territory of issue:",
    passportIssue: "Issue date: mm-dd-yyyy",
    passportExpiry: "Expiry date: mm-dd-yyyy",
    otherDocs: "Do you have any other travel/national ID documents? Please specify:",

    // Citizenship & Residence
    citResTitle: "Citizenship & Residence",
    citizenships: "Country/countries of citizenship:",
    countryResidence: "Country of residence:",
    appliedIRCC: "Have you applied to Immigration, Refugees and Citizenship Canada before?",
    appliedEE: "Have you applied for Express Entry before?",
    uci: "Unique Client Identifier (UCI), if applicable",

    // Family & Funds
    familyTitle: "Family & Funds",
    familyCount: "How many family members do you have? (This includes yourself, spouse or partner, dependent children, and their dependent children.)",
    fundsAmount: "How much money (in Canadian dollars) will you bring to Canada to support yourself and your family?",
    relativeInCanada: "Do you have a relative who is citizen or permanent resident of Canada (The relative must be 18 or older and living in Canada.)",
    referToTable: "Refer to the following table",
    numFamilyMembers: "number of family members",
    fundsNeed: "funds you need (CAD)",
    familyFunds: [
      { members: 1, funds: "$15,263" },
      { members: 2, funds: "$19,001" },
      { members: 3, funds: "$23,360" },
      { members: 4, funds: "$28,362" },
      { members: 5, funds: "$32,168" },
      { members: 6, funds: "$36,280" },
      { members: 7, funds: "$40,392" },
      { members: "Each additional member", funds: "$4,112" },
    ],

    // Education
    educationTitle: "Education history",
    educationIntro:
      "Have you finished high school or any higher education (including university, college or vocational training)? Please answer all the following questions for each secondary and post-secondary periods of study:",
    schoolName: "Name of school/institution:",
    fieldOfStudy: "Field of study:",
    placeOfStudy: "Place of study: Country/province/city",
    postalCode: "Postal code:",
    studyFrom: "Study duration: from mm-dd-yyyy",
    studyTo: "to mm-dd-yyyy",
    currentStudy: "Is this your current study?",
    fullOrPart: "Is this full-time or part-time study?",
    fullTime: "full-time",
    partTime: "part-time",
    completeYears: "Complete/full academic years:",
    eduLevel: "Level of education",
    eduLevelOptions: [
      "Less than secondary school (high school)",
      "Secondary diploma (high school graduation)",
      "One-year degree, diploma or certificate",
      "Two-year program",
      "Bachelor's degree OR 3+ year program",
      "Two or more certificates/diplomas/degrees (one 3+ years)",
      "Master's degree or professional degree",
      "Doctoral level university degree (Ph.D.)",
    ],
    standing: "Standing at the end of study period",
    standingOptions: [
      "Certificate or diploma or degree awarded",
      "In good standing but no certificate or diploma or degree awarded",
      "Not completed",
    ],
    canadianDegreeAwarded: "Were you awarded a Canadian degree, diploma or certificate for this period of study?",
    ecaWithin5: "Have you had an Educational Credential Assessment (ECA) within the past five years for this degree, diploma or certificate?",

    // Languages
    languagesTitle: "Official languages",
    canCommunicate: "Can you communicate in English and/or French?",
    takenEnglishTest: "Have you taken an approved language test to assess your English skills?",
    englishTests: ["IELTS", "CELPIP", "PEARSON"],
    takenFrenchTest: "Have you taken an approved language test to assess your French skills?",
    frenchTests: ["TEF", "TCF"],

    // Province
    provinceTitle: "Which province or territory do you plan to live in?",
    provinces: [
      "Alberta",
      "British Columbia",
      "Manitoba",
      "New Brunswick",
      "Newfoundland and Labrador",
      "Northwest Territories",
      "Nova Scotia",
      "Nunavut",
      "Ontario",
      "Prince Edward Island",
      "Saskatchewan",
      "Yukon",
      "All provinces and territories",
    ],
    nomination: "Have you received a nomination certificate from a province or territory?",

    // Canadian certificate
    certTitle: "Canadian certificate of qualification",
    certProvince: "Province or territory of issuance:",
    certOccupation: "Occupation associated with the certificate (Five digit NOC):",
    certDate: "Date you obtained the certificate: mm-dd-yyyy",

    // Job offer
    jobTitle: "Job offer in Canada",
    jobFulltime: "Is this job offer for work full-time, continuous and for at least one year?",
    jobNonSeasonal: "If yes, is this job offer for work that is non-seasonal?",
    jobNOC: "Five-digit NOC code:",
    jobTitleRole: "Job title:",
    jobEmployer: "Employer or company name:",
    jobContactAddress: "Contact address of employer or company:",
    jobLocation: "Province(territory)/city(town)/street address:",
    jobPostal: "Postal code:",
    jobLMIA: "Was a LMIA issued for the job offer in Canada?",
    jobHowManyEmployers: "How many employers made this job offer?",
    jobEmployerTypes: "Are the employers any of the following (select all that apply):",
    jobEmployerTypeOptions: [
      "An embassy, consulate or high commission in Canada",
      "An employer on the list of ineligible employers with the Government of Canada",
    ],

    // Work experience
    workTitle: "Work experience",
    currentlyWorkingCanada: "Are you currently working in Canada?",
    workHistoryIntro:
      "Work history: Please answer the following for each period of work history.",
    workFromTo: "From  to ",
    workHours: "Hours per week",
    workNOC: "Five-digit NOC code",
    workJobTitle: "Job title",
    workEmployerName: "Employer/company name (indicate if self-employed)",
    workLocation: "Country(territory)/city(town)/address",
    workIfCanada: "If this is work experience in Canada,",
    workCanadaDetails: "i. Are you working legally in Canada during this time?(A person must have: been working on a valid work permit or exempt from needing one. Had legal status the whole time they were in Canada, and did not stay longer than the approved time period.)",
    workStudyingFullTime: "ii. Are you studying full-time while doing this job? (A person must be in class at least 15 hours per week during the academic year to be considered full-time. This includes any period of training in the workplace that is part of the study program.)",
    workCategory: "Which of these categories does your job fall under?",
    workCategoryOptions: [
      "No work permit required",
      "Work permit required, no LMIA required",
      "Work permit and LMIA required",
    ],

    // Research
    researchTitle: "Research",
    researchQ1:
      "1. Have you looked for a job in Canada in your primary occupation? What best describes the last action you took?",
    researchQ1Options: [
      "Yes - I have done some research to find a job in Canada",
      "Yes - I have applied to jobs in Canada",
      "Yes - I have a job offer in Canada",
      "I'm already working in Canada (in my primary occupation)",
      "No - I have not looked for a job in Canada",
      "No - I do not plan to work in Canada",
    ],
    researchQ2:
      "2. Have you checked to see if your education and work experience in your primary occupation would be accepted in Canada?",
    researchQ3:
      "a. With whom did you check? Mark all that apply.",
    researchQ3Options: [
      "Employer when I applied for a job",
      "Work-related or professional organization",
      "School",
      "Friend/relative/host/sponsor",
      "Immigration or visa officer",
      "A federal or provincial government department or service",
      "An immigration lawyer or consultant",
      "An organization that provides settlement or immigrant employment services"
    ],
    researchQ4:
      "3. Some jobs are regulated in Canada. You would need a license or certification to practice these jobs. Do you know if your primary occupation or trade is regulated?",
    researchQ4IfYes:
      "If yes, What have you done so far to get this license or certificate? Mark all that apply.",
    researchQ4Options: [
      "Contacted the regulatory body that issues licenses or certificates for information on the process",
      "Sent my education credentials, proof of skills or work experience to be assessed by the regulatory body that issues licenses or certificates",
      "Completed all education, language, or skills upgrade requirements",
      "Passed all oral, written and practical exams",
      "Completed internship or work experience requirements",
      "Passed all licensing requirements and got a license or certificate to practice"
    ],

    // Declaration
    declarationTitle: "Applicant’s Declaration",
    declarationText:
      "I hereby declare and confirm that I have truthfully and completely provided all the information required in this assessment questionnaire. I authorize and agree that MV may use this information to create my Express Entry (EE) profile. I fully understand that any false, inaccurate, incomplete, or misleading information may result in discrepancies between the EE score and the actual situation. I voluntarily assume full responsibility for any adverse consequences arising therefrom (including but not limited to application refusal or contract termination) and agree that your company may handle my service eligibility and related fees in accordance with the terms of the contract.",
    signature: "Signature",
    submit: "Submit",
  },

  fr: {
    yes: "Oui",
    no: "Non",

    // Profile Details
    pageTitle: "Questionnaire Entrée Express - Demandeur Principal",
    langLabel: "Langue",
    profileTitle: "Détails personnels",
    lastName: "Nom(s) :",
    firstName: "Prénom(s) :",
    gender: "Genre:",
    genderOptions: [
      "Homme",
      "Femme",
      "Autre"
    ],
    dob: "Date de naissance : mm-jj-aaaa",
    pob: "Lieu de naissance : pays / ville (ville)",
    marital: "État matrimonial :",
    maritalOptions: [
      "Jamais marié / célibataire",
      "Marié",
      "Conjoint de fait",
      "Séparé légalement",
      "Divorcé",
      "Veuf",
      "Mariage annulé",
    ],
    accompany: "Si vous êtes en union de fait ou marié, votre époux / conjoint de fait vous accompagnera-t-il au Canada?",

    // Passport 
    passportTitle: "Passeport / Documents de voyage",
    passportNumber: "Numéro de passeport :",
    passportCountry: "Pays / territoire de délivrance :",
    passportIssue: "Date d'émission : mm-jj-aaaa",
    passportExpiry: "Données d'expiration : mm-jj-aaaa",
    otherDocs: "Avez-vous d'autres documents de voyage / d'identité nationale? Veuillez préciser :",

    // Citizenship & Residence
    citResTitle: "Citoyenneté et résidence",
    citizenships: "Pays / pays de citoyenneté :",
    countryResidence: "Pays de résidence :",
    appliedIRCC: "Avez-vous déjà présenté une demande à Immigration, Réfugiés et Citoyenneté Canada?",
    appliedEE: "Avez-vous déjà fait une demande d'Entrée express?",
    uci: "Identifiant client unique (UCI), le cas échéant",

    // Family Funds
    familyTitle: "Famille et fonds",
    familyCount: "Combien de membres de votre famille avez-vous? (Cela inclut vous-même, votre conjoint ou partenaire, vos enfants à charge et leurs enfants à charge.)",
    fundsAmount: "Combien d'argent (en dollars canadiens) apporterez-vous au Canada pour subvenir à vos besoins et à ceux de votre famille?",
    relativeInCanada: "Avez-vous un parent qui est citoyen ou résident permanent du Canada (Le parent doit avoir 18 ans ou plus et vivre au Canada.)",
    referToTable: "Reportez-vous au tableau suivant",
    numFamilyMembers: "nombre de membres de la famille",
    fundsNeed: "fonds dont vous avez besoin (CAD)",
    familyFunds: [
      { members: 1, funds: "$15,263" },
      { members: 2, funds: "$19,001" },
      { members: 3, funds: "$23,360" },
      { members: 4, funds: "$28,362" },
      { members: 5, funds: "$32,168" },
      { members: 6, funds: "$36,280" },
      { members: 7, funds: "$40,392" },
      { members: "si plus de sept personnes, pour chaque membre de la famille supplémentaire", funds: "$4,112" },
    ],


    //Educations
    educationTitle: "Histoire de l'éducation",
    educationIntro:
      "Avez-vous terminé vos études secondaires ou vos études supérieures (y compris une formation universitaire, collégiale ou professionnelle)? Veuillez répondre à toutes les questions suivantes pour chaque période d'études secondaires et postsecondaires :",
    schoolName: "Nom de l'école / institution :",
    fieldOfStudy: "Domaine d'études :",
    placeOfStudy: "Lieu d'étude : Pays / province / ville",
    postalCode: "Code postal :",
    studyFrom: "Durée de l'étude : de mm-jj-aaaa",
    studyTo: "à mm-jj-aaaa",
    currentStudy: "Est-ce votre étude actuelle?",
    fullOrPart: "Est-ce une étude à temps plein ou à temps partiel?",
    fullTime: "à temps plein",
    partTime: "à temps partiel",
    completeYears: "Années académiques complètes / complètes :",
    eduLevel: "Niveau de scolarité",
    eduLevelOptions: [
      "Aucune ou études secondaires non complétées",
      "Diplôme d'études secondaire",
      "Diplôme d'études postsecondaires nécessitant une année d'études",
      "Diplôme d'études postsecondaires nécessitant deux années d'études",
      "Diplôme d'études postsecondaires nécessitant au moins trois années d'études",
      "Diplôme universitaire de deuxième cycle OU diplôme professionnel de premier échelon",
      "Diplôme universitaire de troisième cycle (Ph.D.)",
    ],
    standing: "Résultats :",
    standingOptions: [
      "Certificat ou diplôme délivré",
      "En règle mais aucun certificat ou diplôme délivré",
      "Inachevées",
    ],
    canadianDegreeAwarded: "Avez-vous obtenu un diplôme, un certificat ou un grade canadien pour cette période d’études?",
    ecaWithin5: "Avez-vous a obtenu une évaluation des diplômes d'études (EDE), produit par un organisme désigné au cours des cinq dernières années, pour ce diplôme, certificat ou titre de compétence étranger?",

    languagesTitle: "Langues officielles",
    canCommunicate: "Pouvez-vous communiquer en anglais et / ou en français?",
    takenEnglishTest: "Avez-vous passé un test de langue approuvé pour évaluer vos compétences en anglais?",
    englishTests: ["IELTS", "CELPIP", "PEARSON"],
    takenFrenchTest: "Avez-vous passé un test de langue approuvé pour évaluer vos compétences en français?",
    frenchTests: ["TEF", "TCF"],

    provinceTitle: "Dans quelle province ou territoire comptez-vous vivre?",
    provinces: [
      "Alberta",
      "Colombie-Britannique",
      "Manitoba",
      "Nouveau-Brunswick",
      "Terre-Neuve-et-Labrador",
      "Territoires du Nord-Ouest",
      "Nouvelle-Écosse",
      "Nunavut",
      "Ontario",
      "Île-du-Prince-Édouard",
      "Saskatchewan",
      "Yukon",
      "toutes les provinces et les territoires",
    ],
    nomination: "Avez-vous reçu un certificat de nomination d'une province ou d'un territoire?",

    certTitle: "Certificat de qualification canadien",
    certProvince: "Province ou territoire de délivrance :",
    certOccupation: "Profession associée au certificat (CNP à cinq chiffres) :",
    certDate: "Date à laquelle vous avez obtenu le certificat : mm-jj-aaaa",

    jobTitle: "Offre d'emploi au Canada",
    jobFulltime: "Cette offre d'emploi est-elle pour un travail à temps plein, continu et pendant au moins un an?",
    jobNonSeasonal: "Si oui, cette offre d'emploi est-elle pour un travail non saisonnier?",
    jobNOC: "Code NOC à cinq chiffres :",
    jobTitleRole: "Titre du poste :",
    jobEmployer: "Nom de l'employeur ou de l'entreprise :",
    jobContactAddress: "Adresse de contact de l'employeur ou de l'entreprise :",
    jobLocation: "Province (territoire) / ville (ville) / adresse municipale :",
    jobPostal: "Code postal :",
    jobLMIA: "Une EIMT a-t-elle été émise pour l'offre d'emploi au Canada?",
    jobHowManyEmployers: "Combien d'employeurs ont fait cette offre d'emploi?",
    jobEmployerTypes: "Les employeurs sont-ils parmi les suivants (sélectionnez tous ceux qui s'appliquent) :",
    jobEmployerTypeOptions: [
      "Une ambassade, un consulat ou un haut-commissariat au Canada",
      "Un employeur sur la liste des employeurs inadmissibles du gouvernement du Canada",
    ],

    workTitle: "Expérience de travail",
    currentlyWorkingCanada: "Travaillez-vous actuellement au Canada?",
    workHistoryIntro: "Historique de travail : Veuillez répondre pour chaque période.",
    workFromTo: "De aaaa-mm à aaaa-mm",
    workHours: "Heures par semaine",
    workNOC: "Code NOC à cinq chiffres",
    workJobTitle: "Titre du poste",
    workEmployerName: "Nom de l'employeur / de l'entreprise (indiquer si travail autonome)",
    workLocation: "pays (territoire) / ville (ville) / adresse municipale",
    workIfCanada: "S'il s'agit d'une expérience de travail au Canada,",
    workCanadaDetails: "i. Travaillez-vous légalement au Canada pendant cette période? (Une personne doit avoir: avoir travaillé en vertu d'un permis de travail valide ou en être dispensée. Avoir possédé un statut légal pendant toute la période passée au Canada, et ne pas être restée plus longtemps que la période de temps approuvé.",
    workStudyingFullTime: "ii. Étudier à temps plein tout en occupant cet emploi?(Une personne doit être en classe au moins 15 heures par semaine pendant l'année scolaire pour être considérée comme étudiante à temps plein. Cela comprend toute période de formation en milieu de travail qui fait partie du programme d'études.)",
    workCategory: "Dans laquelle de ces catégories votre travail appartient-il?",
    workCategoryOptions: [
      "Aucun permis de travail requis",
      "Permis de travail requis, aucune EIMT requise",
      "Permis de travail et EIMT requises",
    ],

    researchTitle: "Recherche",
    researchQ1: "1. Avez-vous cherché un emploi au Canada dans votre profession principale? Qu'est-ce qui décrit le mieux la dernière action que vous avez entreprise?",
    researchQ1Options: [
      "Oui, j'ai fait des recherches pour trouver un emploi au Canada",
      "Oui, j'ai postulé à des emplois au Canada",
      "Oui, j'ai une offre d'emploi au Canada",
      "Je travaille déjà au Canada (dans ma profession principale)",
      "Non, je n'ai pas cherché d'emploi au Canada",
      "Non, je n'ai pas l'intention de travailler au Canada",
    ],
    researchQ2:
      "2. Avez-vous vérifié si vos titres de compétences et votre expérience de travail dans votre profession principale seraient reconnus au Canada?",
    researchQ3:
      "a. Avec qui avez-vous vérifié? Cochez tout ce qui s'applique.",
    researchQ3Options: [
      "Employeur lorsque j'ai postulé pour un emploi",
      "Organisme professionnel ou lié au marché travail",
      "Établissement d'enseignement",
      "Ami/parenté/hôte/répondant",
      "Agent d'immigration ou agent des visas",
      "Ministère ou point de service du gouvernement fédéral ou provincial",
      "Avocat ou consultant en immigration",
      "Organisme offrant des services d'établissement ou de recherche d'emploi"
    ],
    researchQ4:
      "3. Certains emplois sont réglementés au Canada. Vous auriez besoin d'un permis ou d'une certification pour exercer ces emplois. Savez-vous si votre profession ou métier principal est réglementé?",
    researchQ4IfYes:
      "Si oui, Quelles démarches avez-vous entreprises pour obtenir une licence ou un certificat de reconnaissance professionnelle? Cochez toutes les réponses qui s'appliquent.",
    researchQ4Options: [
      "J'ai contacté un organisme de réglementation afin d'obtenir de l'information sur le processus de délivrance des licences ou des certificats de reconnaissance professionnelle",
      "J'ai soumis mes titres de compétences ou preuves d'aptitudes ou d'expérience professionnelle aux fins d'évaluation par un organisme de réglementation",
      "J'ai rempli toutes les exigences relatives à la mise à niveau de mes études, de ma connaissance des langues officielles ou de mes compétences",
      "J'ai réussi tous les examens oraux, écrits et pratiques",
      "J'ai rempli toutes les exigences liées aux stages ou à l'expérience de travail",
      "J'ai rempli toutes les exigences de reconnaissance professionnelle et j'ai obtenu une licence ou un certificat afin d'exercer ma profession ou mon métier au Canada",
      "La licence obtenue dans mon pays d'origine a été pleinement reconnue et j'ai obtenu une licence pour exercer ma profession au Canada et ce, sans avoir besoin de mise à niveau de mes compétences"
    ],

    declarationTitle: "Déclaration du candidat",
    declarationText: `Je déclare et confirme avoir fourni de manière véridique et complète toutes les informations requises dans le présent questionnaire d’évaluation. J’autorise et consens à ce que MV utilise ces informations pour la création de mon profil Entrée Express (EE). Je comprends clairement que toute information fausse, inexacte, incomplète ou trompeuse peut entraîner une discordance entre le score EE et la situation réelle. J’assume volontairement l’entière responsabilité de toute conséquence défavorable en résultant (y compris, sans s’y limiter, un refus de demande ou la résiliation du contrat) et j’accepte que votre société traite mes droits de service et les frais correspondants conformément aux dispositions du contrat.`,
    signature: "Signature",
    submit: "Soumettre",
  },
};

export default function PrincipalApplicantForm({
  form,
  setForm,
  setHasSpouse,
  lang
}) {
  const t = T[lang];

  function addEducation() {
    setForm((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        {
          school: "",
          field: "",
          place: "",
          postal: "",
          from: "",
          to: "",
          current: false,
          fullTime: "",
          years: "",
          level: "",
          standing: "",
          canadianAward: "",
          eca: "",
          canadianSchoolType: "",
          languageMajority: "",
          awardReturnHome: "",
          inPerson50: "",
          fullTime8Months: "",
          study8Months: "",
        },
      ],
    }));
  }

  function updateEducation(idx, key) {
    return (e) => {
      const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm((prev) => {
        const newEd = [...prev.education];
        newEd[idx] = { ...newEd[idx], [key]: val };
        return { ...prev, education: newEd };
      });
    };
  }

  function removeEducation(idx) {
    setForm((prev) => {
      const newEd = prev.education.filter((_, index) => index !== idx);
      return { ...prev, education: newEd };
    });
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">{t.pageTitle}</Typography>
      </Box>

      {/* PROFILE */}
      <Typography variant="h6">{t.profileTitle}</Typography>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={2}>
          <TextField fullWidth label={t.lastName} value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} required />
          <TextField fullWidth label={t.firstName} value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} required />
        </Stack>

        <FormControl component="fieldset">
          <FormLabel component="legend">{t.gender}</FormLabel>
          <RadioGroup value={form.gender} onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}>
            {t.genderOptions.map((opt) => (
              <FormControlLabel key={opt} value={opt} control={<Radio required />} label={opt} />
            ))}
          </RadioGroup>
        </FormControl>
        <TextField label={t.dob} type="date" slotProps={{ inputLabel: { shrink: true } }} value={form.dob} onChange={(e) => setForm((p) => ({ ...p, dob: e.target.value }))} required />

        <TextField fullWidth label={t.pob} value={form.pob} onChange={(e) => setForm((p) => ({ ...p, pob: e.target.value }))} required />

        <FormControl component="fieldset">
          <FormLabel component="legend">{t.marital}</FormLabel>
          <RadioGroup row value={form.marital} onChange={(e) => { setForm((p) => ({ ...p, marital: e.target.value, accompanying: "" })); setHasSpouse(false) }}>
            {t.maritalOptions.map((m) => (
              <FormControlLabel key={m} value={m} control={<Radio required />} label={m} />
            ))}
          </RadioGroup>
        </FormControl>

        {(form.marital === t.maritalOptions[1] || form.marital === t.maritalOptions[2]) && (
          <FormControl component="fieldset">
            <FormLabel component="legend">{t.accompany}</FormLabel>
            <RadioGroup row value={form.accompanying} onChange={(e) => { setForm((p) => ({ ...p, accompanying: e.target.value })); setHasSpouse(e.target.value === t.yes) }}>
              <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
              <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
            </RadioGroup>
          </FormControl>
        )}
      </Stack>

      <Divider sx={{ my: 3 }} />
      {/* PASSPORT */}
      <Typography variant="h6">{t.passportTitle}</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField fullWidth label={t.passportNumber} value={form.passportNumber} onChange={(e) => setForm((p) => ({ ...p, passportNumber: e.target.value }))} required />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField fullWidth label={t.passportCountry} value={form.passportCountry} onChange={(e) => setForm((p) => ({ ...p, passportCountry: e.target.value }))} required />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField fullWidth label={t.passportIssue} type="date" slotProps={{ inputLabel: { shrink: true } }} value={form.passportIssue} onChange={(e) => setForm((p) => ({ ...p, passportIssue: e.target.value }))} required />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField fullWidth label={t.passportExpiry} type="date" slotProps={{ inputLabel: { shrink: true } }} value={form.passportExpiry} onChange={(e) => setForm((p) => ({ ...p, passportExpiry: e.target.value }))} required />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography> {t.otherDocs}</Typography>
          <TextField fullWidth value={form.otherIDs} onChange={(e) => setForm((p) => ({ ...p, otherIDs: e.target.value }))} multiline rows={2} required />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />
      {/* CITIZENSHIP */}
      <Typography variant="h6">{t.citResTitle}</Typography>
      <Grid container spacing={2}>
        <TextField fullWidth required label={t.citizenships} value={form.citizenship} onChange={(e) => setForm((p) => ({ ...p, citizenship: e.target.value }))} />
        <TextField fullWidth required label={t.countryResidence} value={form.residence} onChange={(e) => setForm((p) => ({ ...p, residence: e.target.value }))} />

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">{t.appliedIRCC}</FormLabel>
          <RadioGroup required row value={form.appliedIRCC} onChange={(e) => setForm((p) => ({ ...p, appliedIRCC: e.target.value }))}>
            <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
            <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">{t.appliedEE}</FormLabel>
          <RadioGroup required row value={form.appliedEE} onChange={(e) => setForm((p) => ({ ...p, appliedEE: e.target.value }))}>
            <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
            <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
          </RadioGroup>
        </FormControl>

        <Typography> {t.uci} </Typography>
        <TextField fullWidth value={form.uci} onChange={(e) => setForm((p) => ({ ...p, uci: e.target.value }))} />
      </Grid>

      <Divider sx={{ my: 3 }} />
      {/* FAMILY & FUNDS */}
      <Typography variant="h6">{t.familyTitle}</Typography>
      <Grid container spacing={2}>
        <label>
          <Typography> {t.familyCount} </Typography>
          <TextField required fullWidth value={form.familyMembers} onChange={(e) => setForm((p) => ({ ...p, familyMembers: e.target.value }))} />
        </label>
        <Stack direction="column">
          <label>
            <Typography> {t.fundsAmount} </Typography>
            <TextField required fullWidth value={form.funds} onChange={(e) => setForm((p) => ({ ...p, funds: e.target.value }))} />
          </label>
          <Box>
            <Typography> {t.referToTable} </Typography>
            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell> {t.numFamilyMembers} </TableCell>
                    <TableCell> {t.fundsNeed} </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {t.familyFunds.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.members}</TableCell>
                      <TableCell>{row.funds}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend"> {t.relativeInCanada}  </FormLabel>
          <RadioGroup required row value={form.relativeInCanada} onChange={(e) => setForm((p) => ({ ...p, relativeInCanada: e.target.value }))}>
            <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
            <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Divider sx={{ my: 3 }} />
      {/* EDUCATION */}
      <Typography variant="h6">{t.educationTitle}</Typography>
      <Typography sx={{ mb: 2 }}>{t.educationIntro}</Typography>
      {form.education.map((ed, idx) => (
        <Paper elevation={3} sx={{ p: 2, my: 2 }} key={idx}>
          <Grid container spacing={2}>
            <TextField required fullWidth label={t.schoolName} value={ed.school} onChange={updateEducation(idx, "school")} />

            <TextField required fullWidth label={t.fieldOfStudy} value={ed.field} onChange={updateEducation(idx, "field")} />

            <TextField required fullWidth label={t.placeOfStudy} value={ed.place} onChange={updateEducation(idx, "place")} />

            <TextField required fullWidth label={t.postalCode} value={ed.postal} onChange={updateEducation(idx, "postal")} />

            <TextField required fullWidth label={t.studyFrom} type="date" slotProps={{ inputLabel: { shrink: true } }} value={ed.from} onChange={updateEducation(idx, "from")} />

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend"> {t.currentStudy}  </FormLabel>
              <RadioGroup required row value={ed.current} onChange={(e) => {
                setForm((prev) => {
                  const newEd = [...prev.education];
                  newEd[idx].current = e.target.value;
                  if (e.target.value === t.yes) {
                    newEd[idx].to = ""; // disabled and reset study date
                  }
                  return { ...prev, education: newEd };
                });
              }}>
                <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
              </RadioGroup>
            </FormControl>

            <TextField required fullWidth label={t.studyTo} type="date" slotProps={{ inputLabel: { shrink: true } }} value={ed.to} onChange={updateEducation(idx, "to")} disabled={ed.current === t.yes} />

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend"> {t.fullOrPart}  </FormLabel>
              <RadioGroup required row value={ed.fullTime} onChange={updateEducation(idx, "fullTime")}>
                <FormControlLabel value={t.fullTime} control={<Radio required />} label={t.fullTime} />
                <FormControlLabel value={t.partTime} control={<Radio required />} label={t.partTime} />
              </RadioGroup>
            </FormControl>

            <TextField fullWidth required label={t.completeYears} value={ed.years} onChange={updateEducation(idx, "years")} />

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">{t.eduLevel}</FormLabel>
              <RadioGroup value={ed.level} onChange={updateEducation(idx, "level")}>
                {t.eduLevelOptions.map((opt) => (
                  <FormControlLabel key={opt} value={opt} control={<Radio required />} label={opt} />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">{t.standing}</FormLabel>
              <RadioGroup value={ed.standing} onChange={updateEducation(idx, "standing")}>
                {t.standingOptions.map((opt) => (
                  <FormControlLabel key={opt} value={opt} control={<Radio required />} label={opt} />
                ))}
              </RadioGroup>
            </FormControl>

            {/*Awarded Canadian Degree*/}
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend"> {t.canadianDegreeAwarded}  </FormLabel>
              <RadioGroup required row value={ed.canadianAward} onChange={(e) => {
                updateEducation(idx, "canadianAward")({ target: { value: e.target.value } });
                updateEducation(idx, "eca")({ target: { value: "" } });
                updateEducation(idx, "canadianSchoolType")({ target: { value: "" } });
                updateEducation(idx, "languageMajority")({ target: { value: "" } });
                updateEducation(idx, "awardReturnHome")({ target: { value: "" } });
                updateEducation(idx, "inPerson50")({ target: { value: "" } });
                updateEducation(idx, "study8Months")({ target: { value: "" } });
                updateEducation(idx, "fullTime8Months")({ target: { value: "" } });
              }
              }>
                <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
              </RadioGroup>
            </FormControl>

            {/* If no Canadian degree, ask about ECA */}
            {ed.canadianAward === t.no && (
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">{t.ecaWithin5}</FormLabel>
                <RadioGroup required row value={ed.eca} onChange={(e) => updateEducation(idx, "eca")({ target: { value: e.target.value } })}>
                  <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                  <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                </RadioGroup>
              </FormControl>
            )}

            {/* If yes Canadian degree, ask details */}
            {ed.canadianAward === t.yes && (
              <Box sx={{ my: 2 }}>

                <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
                  <FormLabel component="legend">
                    {lang === "en"
                      ? "Was this degree, diploma or certificate: from a public post-secondary school, such as a college, trade, technical school, university orCEGEP (in Quebec)/from a private post-secondary school that operates under the same rules as public schools/a diplôme d'études professionnelles (DEP) or an attestation de spécialisation professionnelle(ASP) earned through a qualifying study program of at least 900 hours from a private school inQuebec, or/a Bachelor's, Master's or Doctorate degree from a Canadian private school that can legallyaward degrees under provincial law. (The student must have been enrolled in a study program that led to a degree as set out by the province.)"
                      : "Est-ce que ce diplôme, diplôme ou certificat : Le diplôme ou le certificat:a t il été décerné par un établissement d'enseignement postsecondaire public, comme un collège, une école de métiers, une école technique, une université ou un cégep (au Québec)?/a t il été décerné par un établissement d'enseignement postsecondaire privé régi par les mêmes règles et règlements qu'un établissement public?  /est il un diplôme d'études professionnelles (DEP) ou une attestation de spécialisation professionnelle (ASP) obtenue par l'intermédiaire d'un programme d'études admissible comptant au moins 900 heures offert par une école privée du Québec? /est il un baccalauréat, une maîtrise ou un doctorat décerné par un établissement d'enseignement privé canadien autorisé à décerner des diplômes au titre de la loi provinciale? (L'étudiant doit avoir été inscrit à un programme d'études menant à un diplôme, conformément aux règlements établis par la province.)"}
                  </FormLabel>
                  <RadioGroup
                    value={ed.canadianSchoolType || ""}
                    onChange={updateEducation(idx, "canadianSchoolType")}>

                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
                  <FormLabel component="legend">
                    {lang === "en"
                      ? "Was more than half the study program English or French as a second language course?"
                      : "Plus de la moitié du programme d'études était-il un cours d'anglais ou de français langue seconde ?"}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={ed.languageMajority || ""}
                    onChange={updateEducation(idx, "languageMajority")}
                  >
                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
                  <FormLabel component="legend">
                    {lang === "en"
                      ? "Did you study in Canada based on an award that required you to go back to your home country to apply your knowledge and skills?"
                      : "Avez-vous étudié au Canada grâce à une bourse qui exigeait que vous retourniez dans votre pays d'origine pour appliquer vos connaissances et compétences ?"}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={ed.awardReturnHome || ""}
                    onChange={updateEducation(idx, "awardReturnHome")}
                  >
                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
                  <FormLabel component="legend">
                    {lang === "en"
                      ? "Did you complete at least 50% of the study or training program's courses through in-person learning?"
                      : "Avez-vous suivi au moins 50 % des cours du programme d'études ou de formation grâce à l'apprentissage en personne?"}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={ed.inPerson50 || ""}
                    onChange={updateEducation(idx, "inPerson50")}
                  >
                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
                  <FormLabel component="legend">
                    {lang === "en"
                      ? "Did you study in Canada for at least 8 months in order to earn this degree, diploma or certificate?"
                      : "Avez-vous étudié au Canada pendant au moins 8 mois afin d'obtenir ce grade, diplôme ou certificat?"}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={ed.study8Months || ""}
                    onChange={updateEducation(idx, "study8Months")}
                  >
                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>

                <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
                  <FormLabel component="legend">
                    {lang === "en"
                      ? "Did you study full-time for at least 8 months to earn this degree, diploma or certificate?"
                      : "Avez-vous étudié à temps plein pendant au moins 8 mois pour obtenir ce diplôme, certificat de diplomate?"}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={ed.fullTime8Months || ""}
                    onChange={updateEducation(idx, "fullTime8Months")}
                  >
                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => removeEducation(idx)} sx={{ mt: 1 }}>
              {lang === "en" ? "Delete this education" : "Supprimer cette formation"}
            </Button>

          </Grid>
        </Paper>
      ))}

      <Button variant="outlined" onClick={addEducation} sx={{ mt: 1 }}>
        {lang === "en" ? "Add another education" : "Ajouter une autre éducation"}
      </Button>

      <Divider sx={{ my: 3 }} />
      {/* LANGUAGES */}
      <Typography variant="h6">{t.languagesTitle}</Typography>
      <Grid container spacing={2}>
        {/*"Can you communicate in English and/or French?"*/}
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">{t.canCommunicate}</FormLabel>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.english}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      english: e.target.checked,
                      primaryLanguage: ""
                    }))
                  }
                />
              }
              label={lang === "en" ? "English" : "Anglais"}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.french}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      french: e.target.checked,
                      primaryLanguage: ""
                    }))
                  }
                />
              }
              label={lang === "en" ? "French" : "Français"}
            />
          </Stack>
        </FormControl>
        {/*   If both, which language are you most at ease using?(French or English)*/}
        {form.english === true && form.french === true && (
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              {lang === "en"
                ? "If both, which language are you most at ease using?(French or English)?"
                : "  Si les deux, dans quelle langue êtes-vous le plus à l'aise? (français ou anglais)"}
            </FormLabel>
            <RadioGroup
              row
              value={form.primaryLanguage || ""}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  primaryLanguage: e.target.value
                }))
              }
            >
              <FormControlLabel value={lang === "en" ? "English" : "Anglais"} control={<Radio required />} label={lang === "en" ? "English" : "Anglais"} />
              <FormControlLabel value={lang === "en" ? "French" : "Français"} control={<Radio required />} label={lang === "en" ? "French" : "Français"} />
            </RadioGroup>
          </FormControl>
        )}

      </Grid>
      {/*2. Have you taken an approved language test to assess your English skills?*/}
      <Grid container spacing={2}>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t.takenEnglishTest}</FormLabel>
          <RadioGroup
            row
            value={form.englishTestTaken || ""}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                englishTestTaken: e.target.value,
                englishTest: "",
                englishTestDate: "",
                englishResultDate: "",
                englishCertNumber: "",
                english_speaking: "",
                english_reading: "",
                english_listening: "",
                english_writing: "",
              }))
            }
          >
            <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
            <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
          </RadioGroup>
        </FormControl>

        {form.englishTestTaken === t.yes && (
          <Stack>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">
                {lang === "en" ? "Language test taken" : "Test de langue passé"}
              </FormLabel>
              <RadioGroup value={form.englishTest} onChange={(e) => setForm((p) => ({ ...p, englishTest: e.target.value }))}>
                {t.englishTests.map((opt) => (
                  <FormControlLabel key={opt} value={opt} control={<Radio required />} label={opt} />
                ))}
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              required
              label={lang === "en" ? "Date of language test : mm-dd-yyyy" : "Date du test de langue : mm-jj-aaaa"}
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={form.englishTestDate || ""}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  englishTestDate: e.target.value
                }))
              }
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              required
              label={lang === "en" ? "Date of test results : mm-dd-yyyy" : "Date des résultats du test : mm-jj-aaaa"}
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={form.englishResultDate || ""}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  englishResultDate: e.target.value
                }))
              }
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              required
              label={lang === "en" ? "Language test result form or certificate number" : "Formulaire de résultat au test de langue ou numéro de certificat :"}
              value={form.englishCertNumber || ""}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  englishCertNumber: e.target.value
                }))
              }
              sx={{ mt: 2 }}
            />

            {/* English test results table */}
            <Typography sx={{ mt: 2 }}>
              {lang === "en" ? "Language test result:" : "Résultat du test de langue :"}
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{lang === "en" ? "Speaking" : "Expression orale"}</TableCell>
                    <TableCell>{lang === "en" ? "Reading" : "Lecture"}</TableCell>
                    <TableCell>{lang === "en" ? "Listening" : "Écoute"}</TableCell>
                    <TableCell>{lang === "en" ? "Writing" : "Écriture"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        required
                        value={form.english_speaking || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            english_speaking: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        required
                        value={form.english_reading || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            english_reading: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        required
                        value={form.english_listening || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            english_listening: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        required
                        value={form.english_writing || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            english_writing: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </Grid>
      {/* French language test */}
      <Grid container spacing={2}>
        <FormControl component="fieldset" sx={{ mt: 2 }}>
          <FormLabel component="legend">{t.takenFrenchTest}</FormLabel>
          <RadioGroup
            row
            value={form.frenchTestTaken || ""}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                frenchTestTaken: e.target.value,
                frenchTest: "",
                frenchTestDate: "",
                frenchResultDate: "",
                frenchCertNumber: "",
                french_speaking: "",
                french_reading: "",
                french_listening: "",
                french_writing: "",
              }))
            }
          >
            <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
            <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
          </RadioGroup>
        </FormControl>

        {form.frenchTestTaken === t.yes && (
          <Stack>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">
                {lang === "en" ? "Language test taken" : "Test de langue passé"}
              </FormLabel>
              <RadioGroup
                value={form.frenchTest}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    frenchTest: e.target.value
                  }))
                }
              >
                {t.frenchTests.map((opt) => (
                  <FormControlLabel key={opt} value={opt} control={<Radio required />} label={opt} />
                ))}
              </RadioGroup>
            </FormControl>
            <TextField
              fullWidth
              required
              label={lang === "en" ? "Date of French language test : mm-dd-yyyy" : "Date du test de français : mm-jj-aaaa"}
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={form.frenchTestDate || ""}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  frenchTestDate: e.target.value
                }))
              }
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              required
              label={lang === "en" ? "Date of French test results : mm-dd-yyyy" : "Date des résultats du test de français : mm-jj-aaaa"}
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={form.frenchResultDate || ""}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  frenchResultDate: e.target.value
                }))
              }
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              required
              label={lang === "en" ? "French test result form or certificate number" : "Formulaire de résultat au test de langue ou numéro de certificat :"}
              value={form.frenchCertNumber || ""}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  frenchCertNumber: e.target.value
                }))
              }
              sx={{ mt: 2 }}
            />

            {/* French test results table */}
            <Typography sx={{ mt: 2 }}>
              {lang === "en" ? "Language test result:" : "Résultat du test de langue :"}
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{lang === "en" ? "Speaking" : "Expression orale"}</TableCell>
                    <TableCell>{lang === "en" ? "Reading" : "Lecture"}</TableCell>
                    <TableCell>{lang === "en" ? "Listening" : "Écoute"}</TableCell>
                    <TableCell>{lang === "en" ? "Writing" : "Écriture"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        required
                        value={form.french_speaking || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            french_speaking: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        required
                        value={form.french_reading || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            french_reading: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        required
                        value={form.french_listening || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            french_listening: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        required
                        value={form.french_writing || ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            french_writing: e.target.value
                          }))
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />
      {/* PROVINCE 1. Which province or territory do you plan to live in? */}
      <Typography variant="h6">{t.provinceTitle}</Typography>
      <FormControl component="fieldset">
        {t.provinces.map((p) => (
          <Grid key={p}>
            <FormControlLabel control={<Radio required checked={form.province === p} onChange={() => setForm((prev) => ({ ...prev, province: p }))} />} label={p} />
          </Grid>
        ))}

      </FormControl>

      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend">{t.nomination}</FormLabel>
        <RadioGroup required row value={form.nomination} onChange={(e) => setForm((p) => ({ ...p, nomination: e.target.value }))}>
          <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
          <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
        </RadioGroup>
      </FormControl>

      <Divider sx={{ my: 3 }} />
      {/*Canadian certificate of qualification */}
      <Typography variant="h6">{t.certTitle}</Typography>
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend">
          {lang === "en"
            ? "Do you have a certificate of qualification from a Canadian province or territory?"
            : "Avez-vous un certificat de qualification d'une province ou d'un territoire canadien?"}
        </FormLabel>
        <RadioGroup
          row
          value={form.hasCertQualification || ""}
          onChange={(e) => setForm((p) => ({
            ...p,
            hasCertQualification: e.target.value,
            certProvince: "",
            certNoc: "",
            certDate: ""
          }))}
        >
          <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
          <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
        </RadioGroup>
      </FormControl>

      {form.hasCertQualification === t.yes && (
        <Grid container spacing={2}>
          <TextField
            fullWidth
            required
            label={t.certProvince}
            value={form.certProvince}
            onChange={(e) => setForm((p) => ({ ...p, certProvince: e.target.value }))}
          />
          <TextField
            fullWidth
            required
            label={t.certOccupation}
            value={form.certNoc}
            onChange={(e) => setForm((p) => ({ ...p, certNoc: e.target.value }))}
          />
          <TextField
            fullWidth
            required
            label={t.certDate}
            type="date"
            slotProps={{ inputLabel: { shrink: true } }}
            value={form.certDate}
            onChange={(e) => setForm((p) => ({ ...p, certDate: e.target.value }))}
          />
        </Grid>
      )}

      <Divider sx={{ my: 3 }} />
      {/* Job offer in Canada */}
      <Typography variant="h6">{t.jobTitle}</Typography>
      <Grid container spacing={2}>
        <FormControl component="fieldset">
          <FormLabel component="legend">
            {lang === "en"
              ? "Do you have a job offer in Canada?"
              : "Avez-vous une offre d'emploi au Canada?"}
          </FormLabel>
          <RadioGroup
            row
            value={form.job.hasJob || ""}
            onChange={(e) => setForm((p) => ({
              ...p, job: {
                ...p.job,
                hasJob: e.target.value,
                fulltime: "",
                nonSeasonal: "",
                noc: "",
                title: "",
                employer: "",
                contactAddress: "",
                location: "",
                postal: "",
                lmia: "",
                howMany: "",
                employerTypes: []
              }
            }))}
          >
            <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
            <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
          </RadioGroup>
        </FormControl>

        {form.job.hasJob === t.yes && (
          <Grid container spacing={2}>
            <FormControl component="fieldset" fullWidth>
              {/* 1. Is this job offer for work full-time, continuous and for at least one year?*/}
              <FormLabel component="legend">{t.jobFulltime}</FormLabel>
              <RadioGroup required row value={form.job.fulltime} onChange={(e) => setForm((p) => ({
                ...p, job: {
                  ...p.job,
                  fulltime: e.target.value,
                  nonSeasonal: ""
                }
              }))}>
                <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
              </RadioGroup>
            </FormControl>

            {form.job.fulltime === t.yes && (
              <FormControl component="fieldset" fullWidth>
                {/*   1. If yes, is this job offer for work that is non-seasonal? */}
                <FormLabel component="legend">{t.jobNonSeasonal}</FormLabel>
                <RadioGroup required row value={form.job.nonSeasonal} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, nonSeasonal: e.target.value } }))}>
                  <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                  <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                </RadioGroup>
              </FormControl>
            )}

            <TextField fullWidth required label={t.jobNOC} value={form.job.noc} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, noc: e.target.value } }))} />

            <TextField fullWidth required label={t.jobTitleRole} value={form.job.title} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, title: e.target.value } }))} />

            <TextField fullWidth required label={t.jobEmployer} value={form.job.employer} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, employer: e.target.value } }))} />

            <TextField fullWidth required label={t.jobContactAddress} value={form.job.contactAddress} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, contactAddress: e.target.value } }))} />

            <TextField fullWidth required label={t.jobLocation} value={form.job.location} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, location: e.target.value } }))} />

            <TextField fullWidth required label={t.jobPostal} value={form.job.postal} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, postal: e.target.value } }))} />

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">{t.jobLMIA}</FormLabel>
              <RadioGroup required row value={form.job.lmia} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, lmia: e.target.value } }))}>
                <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
              </RadioGroup>
            </FormControl>

            <TextField fullWidth required label={t.jobHowManyEmployers} value={form.job.howMany} onChange={(e) => setForm((p) => ({ ...p, job: { ...p.job, howMany: e.target.value } }))} />

            <FormControl component="fieldset" fullWidth>
              <FormLabel>{t.jobEmployerTypes}</FormLabel>
              <Stack direction="column">
                {t.jobEmployerTypeOptions.map((opt) => (
                  <FormControlLabel key={opt} control={<Checkbox checked={form.job.employerTypes.includes(opt)} onChange={(e) => {
                    setForm((p) => {
                      const includes = p.job.employerTypes.includes(opt);
                      return {
                        ...p,
                        job: {
                          ...p.job,
                          employerTypes: includes ? p.job.employerTypes.filter((x) => x !== opt) : [...p.job.employerTypes, opt],
                        },
                      };
                    });
                  }} />} label={opt} />
                ))}
              </Stack>
            </FormControl>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />
      {/* WORK EXPERIENCE */}
      <Typography variant="h6">{t.workTitle}</Typography>
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend">{t.currentlyWorkingCanada}</FormLabel>
        <RadioGroup
          row
          value={form.currentlyWorkingCanada || ""}
          onChange={(e) => setForm((p) => ({ ...p, currentlyWorkingCanada: e.target.value }))}
        >
          <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
          <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
        </RadioGroup>
      </FormControl>

      <Typography sx={{ mb: 2 }}>{t.workHistoryIntro}</Typography>
      {form.work.map((w, idx) => (
        <Paper elevation={3} key={idx} sx={{ p: 2, my: 2 }}>
          <Stack direction="column" spacing={1}>
            <TextField
              fullWidth
              label={t.workFromTo}
              placeholder={lang === "en" ? "From " : "De aaaa-mm"}
              type="month"
              slotProps={{ inputLabel: { shrink: true } }}
              value={w.from}
              onChange={(e) =>
                setForm((p) => {
                  const arr = [...p.work];
                  arr[idx].from = e.target.value;
                  return { ...p, work: arr };
                })
              }
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={w.current === t.yes}
                  onChange={(e) =>
                    setForm((p) => {
                      const arr = [...p.work];
                      arr[idx].current = e.target.checked ? t.yes : t.no;
                      if (e.target.checked) { //reset toDate if is current job
                        arr[idx].to = "";
                      }
                      return { ...p, work: arr };
                    })
                  }
                />
              }
              label={lang === "en" ? "Current job" : "Emploi actuel"}
            />
            <TextField
              fullWidth
              label={lang === "en" ? "To " : "à aaaa-mm"}
              type="month"
              slotProps={{ inputLabel: { shrink: true } }}
              value={w.to}
              onChange={(e) =>
                setForm((p) => {
                  const arr = [...p.work];
                  arr[idx].to = e.target.value;
                  return { ...p, work: arr };
                })
              }
              required
              disabled={w.current === t.yes}
            />
            <TextField
              fullWidth
              label={t.workHours}
              value={w.hours}
              onChange={(e) =>
                setForm((p) => {
                  const arr = [...p.work];
                  arr[idx].hours = e.target.value;
                  return { ...p, work: arr };
                })
              }
              required
            />
            <TextField
              fullWidth
              label={t.workNOC}
              value={w.noc}
              onChange={(e) =>
                setForm((p) => {
                  const arr = [...p.work];
                  arr[idx].noc = e.target.value;
                  return { ...p, work: arr };
                })
              }
              required
            />
            <TextField
              fullWidth
              label={t.workJobTitle}
              value={w.title}
              onChange={(e) =>
                setForm((p) => {
                  const arr = [...p.work];
                  arr[idx].title = e.target.value;
                  return { ...p, work: arr };
                })
              }
              required
            />
            <TextField
              fullWidth
              label={t.workEmployerName}
              value={w.employer}
              onChange={(e) =>
                setForm((p) => {
                  const arr = [...p.work];
                  arr[idx].employer = e.target.value;
                  return { ...p, work: arr };
                })
              }
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={w.selfEmployed === t.yes}
                  onChange={(e) =>
                    setForm((p) => {
                      const arr = [...p.work];
                      arr[idx].selfEmployed = e.target.checked ? t.yes : t.no;
                      return { ...p, work: arr };
                    })
                  }
                />
              }
              label={lang === "en" ? "Self-employed" : "Travailleur autonome"}
            />
            <TextField
              fullWidth
              label={t.workLocation}
              value={w.location}
              onChange={(e) =>
                setForm((p) => {
                  const arr = [...p.work];
                  arr[idx].location = e.target.value;
                  return { ...p, work: arr };
                })
              }
              required
            />

            <FormControl component="fieldset" sx={{ mb: 1 }}>
              <FormLabel component="legend">{t.workIfCanada}</FormLabel>
              <RadioGroup
                row
                value={w.isCanadianExperience || ""}
                onChange={(e) =>
                  setForm((p) => {
                    const arr = [...p.work];
                    arr[idx].isCanadianExperience = e.target.value;
                    arr[idx].legalInCanada = "";
                    arr[idx].studyingFullTime = "";
                    arr[idx].category = "";
                    return { ...p, work: arr };
                  })
                }
              >
                <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
              </RadioGroup>
            </FormControl>

            {w.isCanadianExperience === t.yes && (
              <Box>
                {/* Legal In Canada*/}
                <FormControl component="fieldset" sx={{ mb: 1 }}>
                  <FormLabel component="legend">{t.workCanadaDetails}</FormLabel>
                  <RadioGroup
                    row
                    value={w.legalInCanada || ""}
                    onChange={(e) =>
                      setForm((p) => {
                        const arr = [...p.work];
                        arr[idx].legalInCanada = e.target.value;
                        return { ...p, work: arr };
                      })
                    }
                  >
                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>

                {/* Is Full Time*/}
                <FormControl component="fieldset" sx={{ mb: 1 }}>
                  <FormLabel component="legend">{t.workStudyingFullTime}</FormLabel>
                  <RadioGroup
                    row
                    value={w.studyingFullTime || ""}
                    onChange={(e) =>
                      setForm((p) => {
                        const arr = [...p.work];
                        arr[idx].studyingFullTime = e.target.value;
                        return { ...p, work: arr };
                      })
                    }
                  >
                    <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
                    <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ my: 2 }} />
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t.workCategory}</FormLabel>
                  <RadioGroup
                    row
                    value={w.category}
                    onChange={(e) =>
                      setForm((p) => {
                        const arr = [...p.work];
                        arr[idx].category = e.target.value;
                        return { ...p, work: arr };
                      })
                    }
                  >
                    {t.workCategoryOptions.map((opt) => (
                      <FormControlLabel key={opt} value={opt} control={<Radio required />} label={opt} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} sx={{ alignSelf: "flex-start" }} onClick={() =>
              setForm((p) => ({
                ...p,
                work: p.work.filter((_, i) => i !== idx),
              }))
            }
            >
              {lang === "en" ? "Remove work experience" : "Supprimer cette expérience"}
            </Button>
          </Stack>
        </Paper>

      ))}
      <Box sx={{ my: 2 }} />
      <Button variant="outlined" onClick={() => setForm((p) => ({
        ...p, work: [...p.work,
        {
          from: "",
          to: "",
          current: false,
          hours: "",
          noc: "",
          title: "",
          employer: "",
          location: "",
          legalInCanada: "",
          studyingFullTime: "",
          category: "",
        },
        ],
      }))
      }
      >
        {lang === "en" ? "Add another work experience" : "Ajouter une autre expérience de travail"}
      </Button>

      <Divider sx={{ my: 3 }} />
      {/* RESEARCH */}
      <Typography variant="h6">{t.researchTitle}</Typography>
      {/*Q 1*/}
      <FormControl component="fieldset">
        <FormLabel component="legend">{t.researchQ1}</FormLabel>
        <RadioGroup value={form.research.action} onChange={(e) => setForm((p) => ({ ...p, research: { ...p.research, action: e.target.value } }))}>
          {t.researchQ1Options.map((opt) => (
            <FormControlLabel key={opt} value={opt} control={<Radio required />} label={opt} />
          ))}
        </RadioGroup>
      </FormControl>

      <Divider sx={{ my: 2 }} />

      {/*Q 2*/}
      <FormControl component="fieldset" sx={{ my: 2 }}>
        <FormLabel component="legend">{t.researchQ2}</FormLabel>
        <RadioGroup row value={form.research.educationCheck || ""}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              research: {
                ...p.research,
                educationCheck: e.target.value,
                educationCheckWith: []
              },
            }))
          }
        >
          <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
          <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
        </RadioGroup>
      </FormControl>

      {/* Q3 */}
      {form.research.educationCheck === t.yes && (
        <FormControl component="fieldset" required>
          <FormLabel>{t.researchQ3}</FormLabel>
          <Stack>
            {t.researchQ3Options.map((o) => (
              <FormControlLabel
                key={o}
                control={
                  <Checkbox
                    checked={form.research.educationCheckWith.includes(o)}
                    required={form.research.educationCheckWith.length === 0}
                    onChange={() =>
                      setForm((p) => {
                        const arr = p.research.educationCheckWith;
                        const checked = arr.includes(o);
                        return {
                          ...p,
                          research: {
                            ...p.research,
                            educationCheckWith: checked
                              ? arr.filter((x) => x !== o)
                              : [...arr, o],
                          },
                        };
                      })
                    }
                  />
                }
                label={o}
              />
            ))}
          </Stack>
        </FormControl>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Q4 */}
      <FormControl component="fieldset">
        <FormLabel component="legend">{t.researchQ4}</FormLabel>
        <RadioGroup
          row
          value={form.research.regulated}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              research: {
                ...p.research,
                regulated: e.target.value,
                regulatedAction: []
              },
            }))
          }
        >
          <FormControlLabel value={t.yes} control={<Radio required />} label={t.yes} />
          <FormControlLabel value={t.no} control={<Radio required />} label={t.no} />
        </RadioGroup>
        {form.research.regulated === t.yes && (
          <Box sx={{ mt: 2 }}>
            <FormLabel>{t.researchQ4IfYes}</FormLabel>
            <Stack>
              {t.researchQ4Options.map((o) => (
                <FormControlLabel
                  key={o}
                  control={
                    <Checkbox
                      checked={form.research.regulatedAction.includes(o)}
                      onChange={() =>
                        setForm((p) => {
                          const arr = p.research.regulatedAction;
                          const checked = arr.includes(o);
                          return {
                            ...p,
                            research: {
                              ...p.research,
                              regulatedAction: checked
                                ? arr.filter((x) => x !== o)
                                : [...arr, o],
                            },
                          };
                        })
                      }
                    />
                  }
                  label={o}
                />
              ))}
            </Stack>
          </Box>
        )}
      </FormControl>

      <Divider sx={{ my: 3 }} />
      {/* DECLARATION */}
      <Typography variant="h6">{t.declarationTitle}</Typography>
      <Typography sx={{ whiteSpace: "pre-line", mb: 2 }}>{t.declarationText}</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid>
          <TextField
            fullWidth
            label={t.signature}
            value={form.declarationSigned || ""}
            onChange={(e) => setForm((p) => ({ ...p, declarationSigned: e.target.value }))}
            required
          />
        </Grid>
        <Grid>
          <TextField
            fullWidth
            label={lang === "en" ? "Date" : "Date"}
            type="date"
            slotProps={{ inputLabel: { shrink: true } }}
            value={form.declarationDate || ""}
            onChange={(e) => setForm((p) => ({ ...p, declarationDate: e.target.value }))}
            required
          />
        </Grid>
      </Grid>

    </Container>
  );
}